import { Option, TableRow } from '@code/innovance-react-ui-kit';
import React, { createContext, FC, useContext, useReducer } from 'react';

import { ClientInfoNameResponse, DocumentUploadClientInfoResponse } from 'src/front/api/generated';
import { UploadDocumentFormData } from 'src/front/components/document/UploadDocumentModal';

export enum ACTIONS {
  SEARCH_CUSTOMER,
  SET_DOCUMENTS,
  SET_CLIENT_INFO,
  SET_CLIENT_NAME,
  CLEAR,
}

type Action =
  | {
      type: ACTIONS.SEARCH_CUSTOMER;
      payload: {
        customerSearchValues?: CustomerSearchValues;
        cardPageNumber?: number;
      };
    }
  | { type: ACTIONS.SET_DOCUMENTS; payload: { documents?: UploadDocumentFormData[] } }
  | { type: ACTIONS.SET_CLIENT_INFO; payload: { clientInfo?: DocumentUploadClientInfoResponse } }
  | { type: ACTIONS.SET_CLIENT_NAME; payload: { clientName?: ClientInfoNameResponse } }
  | { type: ACTIONS.CLEAR };

interface CustomerSearchValues {
  customerSurname: string;
  customerName: string;
  customerSecondName: string;
  birthDate: Date | null;
  selectedCustomer?: TableRow;

  identityDocumentSerialCode: string;
  identityDocumentSerialNumber: string;
}

interface ClientInfoState {
  customerId?: string;
  customerNumber?: string;
  customerSearchValues?: CustomerSearchValues;
  pageNumber?: number;
  documentTypeValue?: Option | null;
  documents?: UploadDocumentFormData[];
  clientInfo?: DocumentUploadClientInfoResponse;
  clientName?: ClientInfoNameResponse;
}

const initialValues = {};

type ClientInfoDispatch = (action: Action) => void;

const ClientInfoStateContext = createContext<ClientInfoState | undefined>(undefined);
const ClientInfoDispatchContext = createContext<ClientInfoDispatch | undefined>(undefined);

const clientInfoReducer = (state: ClientInfoState, action: Action): ClientInfoState => {
  switch (action.type) {
    case ACTIONS.SEARCH_CUSTOMER:
    case ACTIONS.SET_DOCUMENTS:
    case ACTIONS.SET_CLIENT_INFO:
    case ACTIONS.SET_CLIENT_NAME:
      return {
        ...state,
        ...action.payload,
      };
    case ACTIONS.CLEAR:
      return initialValues;
    default:
      return state;
  }
};

const ClientInfoProvider: FC = ({ children }) => {
  const [state, dispatch] = useReducer(clientInfoReducer, initialValues);

  return (
    <ClientInfoStateContext.Provider value={state}>
      <ClientInfoDispatchContext.Provider value={dispatch}>
        {children}
      </ClientInfoDispatchContext.Provider>
    </ClientInfoStateContext.Provider>
  );
};

export const useCustomerSearchState = (): ClientInfoState => {
  const context = useContext(ClientInfoStateContext);
  if (context === undefined) {
    throw new Error('useCustomerSearchState must be used within a ClientInfoProvider');
  }

  return context;
};

type SearchInput = Pick<
  ClientInfoState,
  'customerId' | 'customerNumber' | 'customerSearchValues' | 'pageNumber'
>;

type Documents = Pick<ClientInfoState, 'documents'>;
type ClientInfo = Pick<ClientInfoState, 'clientInfo'>;
type ClientName = Pick<ClientInfoState, 'clientName'>;

export const useCustomerSearchDispatch = (): ((payload: SearchInput) => void) => {
  const dispatch = useContext(ClientInfoDispatchContext);
  if (dispatch === undefined) {
    throw new Error('useCustomerSearchDispatch must be used within a ClientInfoProvider');
  }

  return (payload: SearchInput) => dispatch({ type: ACTIONS.SEARCH_CUSTOMER, payload });
};

export const useSetDocumentsDispatch = (): ((payload: Documents) => void) => {
  const dispatch = useContext(ClientInfoDispatchContext);
  if (dispatch === undefined) {
    throw new Error('useSetDocumentsDispatch must be used within a ClientInfoProvider');
  }

  return (payload: Documents) => dispatch({ type: ACTIONS.SET_DOCUMENTS, payload });
};

export const useSetClientInfoDispatch = (): ((payload: ClientInfo) => void) => {
  const dispatch = useContext(ClientInfoDispatchContext);
  if (dispatch === undefined) {
    throw new Error('useSetClientInfoDispatch must be used within a ClientInfoProvider');
  }

  return (payload: ClientInfo) => dispatch({ type: ACTIONS.SET_CLIENT_INFO, payload });
};

export const useSetClientNameDispatch = (): ((payload: ClientName) => void) => {
  const dispatch = useContext(ClientInfoDispatchContext);
  if (dispatch === undefined) {
    throw new Error('useSetClientNameDispatch must be used within a ClientInfoProvider');
  }

  return (payload: ClientName) => dispatch({ type: ACTIONS.SET_CLIENT_NAME, payload });
};

export const useClearDispatch = (): (() => void) => {
  const dispatch = useContext(ClientInfoDispatchContext);
  if (dispatch === undefined) {
    throw new Error('useClearDispatch must be used within a ClientInfoProvider');
  }

  return () => dispatch({ type: ACTIONS.CLEAR });
};

export default ClientInfoProvider;
